import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
    $deleteTableColumn,
    $getTableCellNodeFromLexicalNode,
    $getTableColumnIndexFromTableCellNode,
    $getTableNodeFromLexicalNodeOrThrow,
    $getTableRowNodeFromTableCellNodeOrThrow,
    $insertTableColumn,
    $insertTableRow,
    $isTableNode,
    $isTableSelection,
    getTableObserverFromTableElement,
    TableCellHeaderStates,
    TableCellNode,
    TableNode,
    TableRowNode
} from '@lexical/table';
import { $getSelection, $isRangeSelection } from 'lexical';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ResponsiveToolbar from '../components/ResponsiveToolbar';
import { getDropdownCoords, getNewHeaderState, getTableActions } from './lexicaljsEditorUtils';

const BUTTON_SIZE = 35;

type TableCellDropdownProps = {
    anchorElem: HTMLDivElement;
};

export default function TableCellDropdown({ anchorElem }: TableCellDropdownProps) {
    const [editor] = useLexicalComposerContext();
    const [visible, setVisible] = useState(false);
    const [cellNode, setCellNode] = useState<TableCellNode | null>(null);
    const [coords, setCoords] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
    const [headerState, setHeaderState] = useState<number>(TableCellHeaderStates.NO_STATUS);
    const dropdownRef = useRef<HTMLDivElement | null>(null);
    const rafRef = useRef<number | null>(null);

    const updatePositionForCell = useCallback((tableCell: TableCellNode) => {
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        rafRef.current = requestAnimationFrame(() => {
            if (cellNode) {
                const calcCoords = getDropdownCoords(cellNode, anchorElem, BUTTON_SIZE, editor);
                setCoords(calcCoords);
            }
            setCellNode(tableCell);
            setVisible(true);
        });
    }, [anchorElem, cellNode, editor]);

    useEffect(() => {
        if (!editor) return;

        const update = () => {
            editor.getEditorState().read(() => {
                const selection = $getSelection();
                if (!$isRangeSelection(selection) && !$isTableSelection(selection)) {
                    setVisible(false);
                    setCellNode(null);
                    return;
                }

                const anchorNode = selection.anchor.getNode();
                const tableCell = $getTableCellNodeFromLexicalNode(anchorNode);
                if (!tableCell) {
                    setVisible(false);
                    setCellNode(null);
                    return;
                }

                try {
                    $getTableNodeFromLexicalNodeOrThrow(tableCell);
                } catch {
                    setVisible(false);
                    setCellNode(null);
                    return;
                }

                updatePositionForCell(tableCell as TableCellNode);
            });
        };

        const unregister = editor.registerUpdateListener(update);
        update();

        return () => {
            unregister?.();
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, [editor, anchorElem, updatePositionForCell]);

    useEffect(() => {
        if (!cellNode) return;
        try {
            editor.getEditorState().read(() => {
                const latestNode = cellNode.getLatest();
                if (latestNode) {
                    setHeaderState(latestNode.getHeaderStyles());
                }
            });
        } catch (e) {
            setVisible(false);
            setCellNode(null);
            return;
        }

        let frame: number | null = null;
        const onScrollOrResize = () => {
            if (frame) cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => {
                updatePositionForCell(cellNode);
                frame = null;
            });
        };

        const onDocDown = (e: MouseEvent) => {
            const t = e.target as Node;
            if (dropdownRef.current?.contains(t)) return;
            if (anchorElem.contains(t)) {
                if (!dropdownRef.current?.contains(t)) setVisible(false);
                return;
            }
            setVisible(false);
        };

        window.addEventListener('scroll', onScrollOrResize, true);
        window.addEventListener('resize', onScrollOrResize);
        document.addEventListener('mousedown', onDocDown);

        const ro = new ResizeObserver(onScrollOrResize);
        ro.observe(anchorElem);

        return () => {
            window.removeEventListener('scroll', onScrollOrResize, true);
            window.removeEventListener('resize', onScrollOrResize);
            document.removeEventListener('mousedown', onDocDown);
            ro.disconnect();
            if (frame) cancelAnimationFrame(frame);
        };
    }, [cellNode, anchorElem, editor, updatePositionForCell]);

    const onTableActionSelect = useCallback(
        (key: string) => {
            if (!cellNode) return;

            editor.update(() => {
                const cellRow = $getTableRowNodeFromTableCellNodeOrThrow(cellNode);
                const tableNode = cellRow?.getParent();
                if (!tableNode || !$isTableNode(tableNode)) {
                    console.warn('Could not find parent table for cell');
                    return;
                }

                const table = tableNode as TableNode;

                const domElem = editor.getElementByKey(table.getKey());
                if (!domElem) {
                    console.warn('Could not find DOM element for table');
                    return;
                }

                const observer = getTableObserverFromTableElement(domElem as HTMLTableElement);
                if (!observer) {
                    console.warn('Could not find table observer');
                    return;
                }

                const domTable = observer.table;

                const rowIndex = table.getChildren().findIndex((child) => child.getKey() === cellRow.getKey());
                const columnIndex = cellRow.getChildren().findIndex((child) => child.getKey() === cellNode.getKey());

                if (rowIndex === -1 || columnIndex === -1) {
                    console.warn('Could not find cell position in table');
                    return;
                }
                switch (key) {
                    case 'deleteRow': {
                        const row = $getTableRowNodeFromTableCellNodeOrThrow(cellNode);
                        row.remove();
                        return;
                    }

                    case 'deleteColumn': {
                        $deleteTableColumn(table, columnIndex);
                        return;
                    }

                    case 'deleteTable': {
                        table.remove();
                        return;
                    }

                    case 'insertColumnLeft': {
                        $insertTableColumn(table, columnIndex, false, 1, domTable);
                        return;
                    }

                    case 'insertColumnRight': {
                        $insertTableColumn(table, columnIndex, true, 1, domTable);
                        return;
                    }

                    case 'insertRowAbove': {
                        $insertTableRow(table, rowIndex, false, 1, domTable);
                        return;
                    }

                    case 'insertRowBelow': {
                        $insertTableRow(table, rowIndex, true, 1, domTable);
                        return;
                    }
                    case 'toggleRowHeader': {
                        const row = $getTableRowNodeFromTableCellNodeOrThrow(cellNode);
                        row.getChildren().forEach((cell: TableCellNode) => {
                            const currentState = cell.getHeaderStyles();
                            const newState = getNewHeaderState(currentState, 'row');
                            cell.setHeaderStyles(newState);
                        });
                        return;
                    }
                    case 'toggleColumnHeader': {
                        const table = $getTableNodeFromLexicalNodeOrThrow(cellNode);
                        const columnIndex = $getTableColumnIndexFromTableCellNode(cellNode);
                        const rows = table.getChildren() as TableRowNode[];

                        rows.forEach((row) => {
                            const cells = row.getChildren() as TableCellNode[];
                            const cell = cells[columnIndex];
                            if (!cell) return;
                            const currentState = cell.getHeaderStyles();
                            const newState = getNewHeaderState(currentState, 'column');
                            cell.setHeaderStyles(newState);
                        });
                        return;
                    }
                }
            });
        },
        [cellNode, editor]
    );

    if (!visible || !cellNode) return null;

    return createPortal(
        <div
            ref={dropdownRef}
            style={{
                position: 'absolute',
                left: coords.x,
                top: coords.y,
                zIndex: 9999,
                pointerEvents: 'auto'
            }}
            onMouseDown={(e) => e.stopPropagation()}
        >
            <ResponsiveToolbar
                disabled={false}
                isSmall
                selected={undefined}
                onSelect={onTableActionSelect}
                options={getTableActions(cellNode, headerState)}
                multi={false}
            />
        </div>,
        anchorElem
    );
}
